import {
    homePageSettings as blankHomePageSettings,
    layoutSettings as blankLayoutSettings,
} from '@/utils/cms/cms-blank-states'
import {appInstance} from '~/utils/app-accessor'
import {EventBus} from '~/utils/event-bus'
import type {Ref} from 'vue'

export const useCmsStore = defineStore('cms', () => {
    const popularDestinations: Ref<any[]> = ref([])
    const popularHotels: Ref<any[]> = ref([])
    const layoutSettings = ref(blankLayoutSettings())
    const homePageSettings = ref(blankHomePageSettings())
    const blogItems: Ref<any[]> = ref([])
    const topTours: Ref<any[]> = ref([])
    const resorts: Ref<any[]> = ref([])

    async function loadPopularDestinations() {
        try {
            const destinations = await appInstance.$api.popularDestinations.get()
            const promises = destinations.map((destination: {id: any}) =>
                appInstance.$api.locations.get({id: destination.id, limitCities: 0})
            )
            const data = await Promise.all(promises)
            popularDestinations.value = data
                .map((rs, index) => {
                    if (!rs.cities.length) return null
                    const location = rs.cities[0]
                    Object.assign(location, destinations[index])
                    return location
                })
                .filter(Boolean)
        } catch {
            popularDestinations.value = []
        }
    }

    async function loadPopularHotels() {
        try {
            popularHotels.value = await appInstance.$api.popularHotels.get()
        } catch {
            popularHotels.value = []
        }
    }

    async function loadLayoutSettings() {
        const titleTemplate = appInstance.$conf.public.head.titleTemplate
        try {
            const _layoutSettings = await appInstance.$api.layoutSettings.get()
            if (!_layoutSettings.head.titleTemplate) {
                _layoutSettings.head.titleTemplate = titleTemplate
            }
            layoutSettings.value = _layoutSettings
        } catch {
            layoutSettings.value = blankLayoutSettings(titleTemplate)
        }
    }

    async function loadHomePageSettings() {
        try {
            homePageSettings.value = await appInstance.$api.homePageSettings.get()
        } catch {
            homePageSettings.value = blankHomePageSettings()
        }
    }

    async function loadBlogItems() {
        try {
            blogItems.value = await appInstance.$api.blog.get()
        } catch {
            blogItems.value = []
        }
    }

    async function loadTopTours() {
        try {
            topTours.value = await appInstance.$api.topTours.get()
        } catch {
            topTours.value = []
        }
    }

    async function loadResorts() {
        try {
            const _resorts: any[] = await appInstance.$api.resorts.get(),
                cities: any[] = (
                    await Promise.all(
                        _resorts.map(({cityId}) => appInstance.$api.locations.get({id: cityId, limitCities: 1}))
                    )
                ).reduce((cities, {cities: [city]}) => {
                    cities.push(city)
                    return cities
                }, [])
            const countryIds = [...new Set(cities.map(({countryId}) => countryId))],
                countries: any[] = (
                    await Promise.all(countryIds.map(id => appInstance.$api.locations.get({id, limitCountries: 1})))
                ).reduce((countries, {countries: [country]}) => {
                    countries.push(country)
                    return countries
                }, [])
            resorts.value = _resorts.map(({cityId, ...resort}) => {
                const {name: cityName, countryId} = cities.find(({id}) => parseInt(cityId, 10) === id),
                    {iso2Code: countryCode, name: countryName} = countries.find(({id}) => countryId === id)
                return {...resort, cityName, countryCode: countryCode.toLowerCase(), countryName}
            })
        } catch {
            resorts.value = []
        }
    }

    async function serverInit() {
        await Promise.all([loadLayoutSettings()])
    }

    function clientInit() {
        EventBus.$on('CHANGE_LOCALE_EVENT', loadLayoutSettings)
    }

    return {
        popularDestinations,
        popularHotels,
        layoutSettings,
        homePageSettings,
        blogItems,
        topTours,
        resorts,
        loadPopularDestinations,
        loadPopularHotels,
        loadLayoutSettings,
        loadHomePageSettings,
        loadBlogItems,
        loadTopTours,
        loadResorts,
        serverInit,
        clientInit,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useCmsStore, import.meta.hot))
